import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
    ClientUser, 
    GuestUser, 
    LoyaltyTier, 
    LoyaltyReward, 
    LoyaltyPointsTransaction, 
    PurchasedPack,
    FreeAstresBatch,
    ChatMessage, 
    InteractionHistory,
    UserLocation 
} from '../types';
import { socket } from '../services/socket';
import { subscribeToPushNotifications, requestPushPermissionAndSubscribe } from '../services/pushService';

export const getTierForPoints = (points: number): LoyaltyTier => {
    if (points >= 1000) return 'Diamant';
    if (points >= 500) return 'Platine';
    if (points >= 250) return 'Or';
    if (points >= 100) return 'Argent';
    return 'Bronze';
};

export const TIER_CONFIG: Record<LoyaltyTier, {
    label: string;
    minPoints: number;
    nextPoints: number | null;
    multiplier: number;
    color: string;
    badgeBg: string;
    description: string;
}> = {
    Bronze: {
        label: 'Initié Stellaire',
        minPoints: 0,
        nextPoints: 100,
        multiplier: 1.0,
        color: 'text-amber-400 border-amber-500/40 bg-amber-950/30',
        badgeBg: 'from-amber-600 to-amber-800',
        description: 'Accès standard aux fonctionnalités et tirages'
    },
    Argent: {
        label: 'Adepte Cosmique',
        minPoints: 100,
        nextPoints: 250,
        multiplier: 1.2,
        color: 'text-slate-300 border-slate-400/50 bg-slate-900/40',
        badgeBg: 'from-slate-400 to-slate-600',
        description: '+20% de bonus sur les récompenses & badges'
    },
    Or: {
        label: 'Guide Céleste',
        minPoints: 250,
        nextPoints: 500,
        multiplier: 1.5,
        color: 'text-gold-400 border-gold-400/60 bg-gold-950/40',
        badgeBg: 'from-gold-400 to-yellow-600 text-black',
        description: '+50% de bonus & accès prioritaire aux voyants'
    },
    Platine: {
        label: 'Maître des Étoiles',
        minPoints: 500,
        nextPoints: 1000,
        multiplier: 1.8,
        color: 'text-cyan-300 border-cyan-400/60 bg-cyan-950/40',
        badgeBg: 'from-cyan-400 to-blue-600 text-black',
        description: '+80% de bonus & tirage mensuel offert'
    },
    Diamant: {
        label: 'Oracle Cosmique',
        minPoints: 1000,
        nextPoints: null,
        multiplier: 2.0,
        color: 'text-purple-300 border-purple-400/60 bg-purple-950/50',
        badgeBg: 'from-purple-400 via-pink-400 to-indigo-500 text-black',
        description: 'Multiplicateur x2 & consultations VIP exclusives'
    }
};

// =========================================================================
// GRILLE OFFICIELLE DES RECHARGES TRANSCASH (1 € = 10 Astres de base)
// Les astres achetés N'EXPIRENT JAMAIS.
// =========================================================================
export const TRANSCASH_PACKS_CONFIG: Record<'50' | '100' | '150', {
    packType: '50' | '100' | '150';
    price: number;
    baseAstres: number;
    bonusAstres: number;
    totalAstres: number;
    name: string;
    description: string;
    badge?: string;
}> = {
    '50': {
        packType: '50',
        price: 50,
        baseAstres: 500,
        bonusAstres: 50,
        totalAstres: 550,
        name: "Recharge Équilibre 50€",
        description: "500 Astres + 50 Astres offerts (27,5 min d'appel direct)",
        badge: "+50 Astres offerts"
    },
    '100': {
        packType: '100',
        price: 100,
        baseAstres: 1000,
        bonusAstres: 150,
        totalAstres: 1150,
        name: "Recharge Sérénité 100€",
        description: "1 000 Astres + 150 Astres offerts (57,5 min d'appel direct)",
        badge: "+150 Astres offerts"
    },
    '150': {
        packType: '150',
        price: 150,
        baseAstres: 1500,
        bonusAstres: 300,
        totalAstres: 1800,
        name: "Recharge Privilège 150€",
        description: "1 500 Astres + 300 Astres offerts (1h30 d'appel direct)",
        badge: "+300 Astres offerts"
    }
};

// =========================================================================
// GESTIONNAIRE DE PÉREMPTION DES ASTRES GRATUITS (30 JOURS)
// =========================================================================
export const checkAndExpireAstres = (client: ClientUser): ClientUser => {
    const now = Date.now();
    let freeBatches = Array.isArray(client.freeAstresBatches) ? [...client.freeAstresBatches] : [];
    let expiredPointsTotal = 0;
    const validBatches: FreeAstresBatch[] = [];

    freeBatches.forEach(batch => {
        const expiresMs = new Date(batch.expiresAt).getTime();
        if (!isNaN(expiresMs) && expiresMs <= now) {
            if (batch.remaining > 0) {
                expiredPointsTotal += batch.remaining;
            }
        } else if (batch.remaining > 0) {
            validBatches.push(batch);
        }
    });

    const paidAstres = typeof client.paidAstres === 'number' ? Math.max(0, client.paidAstres) : 0;
    const freeAstres = validBatches.reduce((sum, b) => sum + Math.max(0, b.remaining), 0);
    const totalLoyaltyPoints = freeAstres + paidAstres;

    let transactions = Array.isArray(client.loyaltyTransactions) ? [...client.loyaltyTransactions] : [];
    if (expiredPointsTotal > 0) {
        transactions.unshift({
            id: `tx_exp_${now}_${Math.random().toString(36).substring(2, 6)}`,
            date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
            amount: -expiredPointsTotal,
            type: 'expired' as any,
            description: `Péremption de ${expiredPointsTotal} Astres offerts (Validité 30 jours expirée)`
        });
    }

    return {
        ...client,
        freeAstres,
        freeAstresBatches: validBatches,
        paidAstres,
        loyaltyPoints: totalLoyaltyPoints,
        loyaltyTier: getTierForPoints(totalLoyaltyPoints),
        loyaltyTransactions: transactions
    };
};

export const DEFAULT_LOYALTY_REWARDS: LoyaltyReward[] = [
    {
        id: 'rew_100_cartes',
        title: 'Analyse 3 Cartes',
        description: 'Une analyse complète personnalisée en 3 cartes par votre médium favori chat.',
        cost: 100,
        icon: 'sparkles',
        category: 'tarot',
        badge: 'Analyse'
    },
    {
        id: 'rew_200_15min',
        title: '15 Min de plus (Forfait 50€/100€)',
        description: '15 minutes de plus applicables sur les forfaits de 50€ ou 100€.',
        cost: 200,
        icon: 'zap',
        category: 'minutes',
        badge: 'Crédit'
    },
    {
        id: 'rew_350_theme',
        title: 'Étude Astrologique',
        description: 'Étude astrologique détaillée de votre ciel de naissance et prévisions semestrielles.',
        cost: 350,
        icon: 'trophy',
        category: 'vip',
        badge: 'Astrologie'
    },
    {
        id: 'rew_500_seance',
        title: 'Séance Offerte 15 Min',
        description: 'Séance complète offerte de 15 minutes en direct avec le médium de votre choix.',
        cost: 500,
        icon: 'crown',
        category: 'minutes',
        badge: 'Direct'
    },
    {
        id: 'rew_1000_consultation',
        title: 'Consultation 1H30',
        description: '1h30min de consultation complète avec votre médium préféré.',
        cost: 1000,
        icon: 'star',
        category: 'vip',
        badge: 'Prestige'
    }
];

interface ClientContextType {
    client: ClientUser | null;
    loginClient: (email: string, pass: string) => { success: boolean; message?: string };
    registerClient: (name: string, email: string, phone: string, address: string, gender: 'homme' | 'femme', pass: string, birthDate?: string) => boolean;
    quickRegisterClient: (name: string, phone: string, email?: string, address?: string, password?: string) => ClientUser;
    logoutClient: () => void;
    updateClientProfile: (data: Partial<ClientUser>) => void; 
    addHistoryEntry: (entry: InteractionHistory) => void; 
    
    // UI State
    isAuthModalOpen: boolean;
    openAuthModal: (prefillData?: Partial<ClientUser>) => void;
    closeAuthModal: () => void;
    authPrefill: Partial<ClientUser> | null;

    // Reviews
    addReview: (mediumId: number, rating: number, text: string, guestName?: string) => void;
    getRealReviews: (mediumId: number) => Review[];

    // Loyalty Points System
    addLoyaltyPoints: (amount: number, reason: string, mediumName?: string, transactionType?: LoyaltyPointsTransaction['type']) => void;
    recordSessionDurationAndPoints: (sessionId: string, durationMinutes: number, mediumName: string, mediumImage: string, mediumId?: number | string, messages?: ChatMessage[]) => number;
    recordDivinationActivity: (type: 'tarot' | 'dreams' | 'love' | 'horoscope' | 'numerology' | 'crystal', title: string, summary: string, details?: any) => void;
    deductVoiceCallPoints: (durationSeconds: number, mediumName?: string) => { minutesUsed: number; pointsDeducted: number; remainingPoints: number };
    claimDailyBonus: () => { success: boolean; pointsAwarded: number; message: string };
    redeemReward: (rewardId: string) => { success: boolean; message: string };
    rewardsCatalog: LoyaltyReward[];

    // Pack Purchases with Transcash
    buyPack: (packType: '50' | '100' | '150' | string, transcashCode?: string) => { success: boolean; minutesAdded: number; pointsAdded: number };
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

// Helper safe parsing
const safeJsonParse = <T,>(key: string, fallback: T): T => {
    const saved = localStorage.getItem(key);
    if (!saved || saved.trim() === "") return fallback;
    try {
        return JSON.parse(saved);
    } catch (e) {
        console.error(`Error parsing client key "${key}":`, e);
        return fallback;
    }
};

export const ClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [client, setClient] = useState<ClientUser | null>(() => {
        if (typeof window === 'undefined') return null;
        return safeJsonParse<ClientUser | null>('astro_current_client', null);
    });
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [authPrefill, setAuthPrefill] = useState<Partial<ClientUser> | null>(null);
    
    // Initialisation & migration automatique du profil client avec système de fidélité
    useEffect(() => {
        const saved = safeJsonParse<ClientUser | null>('astro_current_client', null);
        if (saved) {
            // Vérifier si le compte est suspendu dans la base de données
            const allClients = safeJsonParse<ClientUser[]>('astro_clients_db', []);
            const dbRecord = allClients.find(c => c.id === saved.id);
            if (dbRecord?.isSuspended) {
                setClient(null);
                localStorage.removeItem('astro_current_client');
        try { localStorage.removeItem('astro_user_registered_v2'); } catch(e){}
                return;
            }

            // Assurer que les champs de fidélité et le portefeuille existent
            let workingClient: ClientUser = { ...saved };
            if (typeof workingClient.freeAstres !== 'number' && typeof workingClient.paidAstres !== 'number') {
                // Migration anciens comptes vers le nouveau système de portefeuille
                const totalPts = typeof workingClient.loyaltyPoints === 'number' ? workingClient.loyaltyPoints : 30;
                workingClient.freeAstres = totalPts;
                workingClient.paidAstres = 0;
                workingClient.freeAstresBatches = [
                    {
                        id: 'batch_init',
                        amount: totalPts,
                        remaining: totalPts,
                        earnedAt: new Date(workingClient.createdAt || Date.now()).toISOString(),
                        expiresAt: new Date(Date.now() + 30 * 86400000).toISOString(),
                        source: 'welcome',
                        description: 'Cadeau de bienvenue (Valable 30 jours)'
                    }
                ];
            }

            // Vérifier et appliquer la péremption à 30 jours
            const migratedClient = checkAndExpireAstres(workingClient);
            setClient(migratedClient);
            localStorage.setItem('astro_current_client', JSON.stringify(migratedClient));
        }
    }, []);

    // Listeners for real-time purge or suspension by admin
    useEffect(() => {
        const handlePurged = (e: any) => {
            if (client && (e.detail?.clientId === client.id)) {
                setClient(null);
                localStorage.removeItem('astro_current_client');
                alert("Votre compte et vos données ont été définitivement supprimés.");
            }
        };

        const handleSuspended = (e: any) => {
            if (client) {
                setClient(null);
                localStorage.removeItem('astro_current_client');
                const reason = e.detail?.reason ? `\nMotif : "${e.detail.reason}"` : '';
                alert(`Votre compte a été suspendu par l'administration.${reason}\nVeuillez contacter le support.`);
            }
        };

        const handleSyncClients = (clients: ClientUser[]) => {
            if (Array.isArray(clients)) {
                try { localStorage.setItem('astro_clients_db', JSON.stringify(clients)); } catch(e){}
                const currentStored = safeJsonParse<ClientUser | null>('astro_current_client', null);
                if (currentStored) {
                    const cleanStoredPhone = (currentStored.phone || '').replace(/\D/g, '');
                    const updatedMe = clients.find(c => 
                        c && (
                            (currentStored.id && c.id === currentStored.id) ||
                            (cleanStoredPhone && c.phone && c.phone.replace(/\D/g, '') === cleanStoredPhone) ||
                            (currentStored.email && c.email && c.email.toLowerCase() === currentStored.email.toLowerCase())
                        )
                    );
                    if (updatedMe) {
                        if (updatedMe.isSuspended) {
                            setClient(null);
                            localStorage.removeItem('astro_current_client');
                            alert(`Votre compte a été suspendu par l'administration.${updatedMe.suspensionReason ? `\nMotif : "${updatedMe.suspensionReason}"` : ''}\nVeuillez contacter le support.`);
                        } else {
                            // Fusionner intelligemment pour ne jamais écraser la date de réclamation du jour ni les points récents
                            const todayStr = new Date().toLocaleDateString('en-CA');
                            const hasLocalClaim = currentStored.claimedDailyBonusDate === todayStr || 
                                localStorage.getItem(`astro_daily_claimed_${currentStored.id}_${todayStr}`) === 'true';
                            
                            const mergedMe: ClientUser = {
                                ...currentStored,
                                ...updatedMe,
                                claimedDailyBonusDate: hasLocalClaim ? todayStr : (updatedMe.claimedDailyBonusDate || currentStored.claimedDailyBonusDate),
                                freeAstres: Math.max(currentStored.freeAstres || 0, updatedMe.freeAstres || 0),
                                paidAstres: Math.max(currentStored.paidAstres || 0, updatedMe.paidAstres || 0),
                                loyaltyPoints: Math.max(currentStored.loyaltyPoints || 0, updatedMe.loyaltyPoints || 0),
                                freeAstresBatches: (currentStored.freeAstresBatches && currentStored.freeAstresBatches.length >= (updatedMe.freeAstresBatches?.length || 0))
                                    ? currentStored.freeAstresBatches
                                    : (updatedMe.freeAstresBatches || currentStored.freeAstresBatches || [])
                            };
                            setClient(mergedMe);
                            try { localStorage.setItem('astro_current_client', JSON.stringify(mergedMe)); } catch(e){}
                        }
                    }
                }
            }
        };

        const handleClientDeleted = (deletedId: string) => {
            const currentStored = safeJsonParse<ClientUser | null>('astro_current_client', null);
            if (currentStored && currentStored.id === deletedId) {
                setClient(null);
                localStorage.removeItem('astro_current_client');
                alert("Votre compte a été définitivement supprimé par l'administration.");
            }
        };

        window.addEventListener('astro_client_purged', handlePurged);
        window.addEventListener('astro_client_suspended', handleSuspended);
        socket.on('sync_clients', handleSyncClients);
        socket.on('client_deleted', handleClientDeleted);

        return () => {
            window.removeEventListener('astro_client_purged', handlePurged);
            window.removeEventListener('astro_client_suspended', handleSuspended);
            socket.off('sync_clients', handleSyncClients);
            socket.off('client_deleted', handleClientDeleted);
        };
    }, [client]);

    const saveClientState = (updatedClient: ClientUser) => {
        // Recalcul automatique du rang de fidélité en fonction des points
        const points = typeof updatedClient.loyaltyPoints === 'number' ? updatedClient.loyaltyPoints : 0;
        const tier = getTierForPoints(points);
        
        // Sauvegarde permanente de l'avatar propre à ce compte
        if (updatedClient.avatar && updatedClient.id) {
            try { localStorage.setItem(`astro_client_avatar_${updatedClient.id}`, updatedClient.avatar); } catch(e){}
        }

        const finalClient: ClientUser = {
            ...updatedClient,
            loyaltyTier: tier
        };

        // 1. Update current state
        setClient(finalClient);
        localStorage.setItem('astro_current_client', JSON.stringify(finalClient));

        // 2. Update DB (Liste complète des clients) & Sync VPS
        const data = safeJsonParse('astro_clients_db', []);
        const users: ClientUser[] = Array.isArray(data) ? data : [];
        const newUsers = users.map(u => u.id === finalClient.id ? finalClient : u);
        localStorage.setItem('astro_clients_db', JSON.stringify(newUsers));

        // 3. Diffuser au serveur VPS et à l'Admin Dashboard
        socket.emit('client_updated', finalClient);
    };

    const loginClient = (email: string, pass: string): { success: boolean; message?: string } => {
        const data = safeJsonParse('astro_clients_db', []);
        const users: ClientUser[] = Array.isArray(data) ? data : [];
        const identifier = (email || '').trim().toLowerCase();
        const rawDigits = identifier.replace(/\D/g, '');
        let found = users.find(u => {
            if (!u) return false;
            const matchEmail = u.email && (u.email.toLowerCase() === identifier);
            const matchName = u.name && (u.name.toLowerCase() === identifier);
            const userPhoneDigits = (u.phone || '').replace(/\D/g, '');
            const matchPhone = u.phone && (
                u.phone.toLowerCase() === identifier || 
                (rawDigits.length >= 8 && userPhoneDigits === rawDigits)
            );
            return (matchEmail || matchName || matchPhone) && u.password === pass;
        });



        if (found) {
            if (found.isSuspended) {
                return {
                    success: false,
                    message: `Ce compte a été suspendu par l'administration${found.suspensionReason ? ` (Motif : ${found.suspensionReason})` : ''}. Veuillez contacter le secrétariat.`
                };
            }
            const points = typeof found.loyaltyPoints === 'number' ? found.loyaltyPoints : 1000;
            const permanentAvatar = (typeof window !== 'undefined' ? localStorage.getItem(`astro_client_avatar_${found.id}`) : null) || found.avatar;
            const finalFound: ClientUser = {
                ...found,
                avatar: permanentAvatar,
                loyaltyPoints: points,
                loyaltyTier: getTierForPoints(points),
                totalSessionMinutes: found.totalSessionMinutes || 500,
                unlockedRewards: found.unlockedRewards || [],
                loyaltyTransactions: found.loyaltyTransactions || [
                    {
                        id: `tx_${Date.now()}`,
                        date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
                        amount: 1000,
                        type: 'bonus',
                        description: 'Compte VIP Master Activé'
                    }
                ]
            };
            setClient(finalFound);
            localStorage.setItem('astro_current_client', JSON.stringify(finalFound));
            socket.emit('client_updated', { ...finalFound, lastActiveTime: Date.now() });
            try { requestPushPermissionAndSubscribe(finalFound); } catch (e) {}
            return { success: true };
        }
        return { success: false, message: "Email ou mot de passe incorrect." };
    };

    const registerClient = (name: string, email: string, phone: string, address: string, gender: 'homme' | 'femme', pass: string, birthDate?: string) => {
        const data = safeJsonParse('astro_clients_db', []);
        const users: ClientUser[] = Array.isArray(data) ? data : [];
        if (users.find(u => u && (u.email || '').toLowerCase() === email.trim().toLowerCase())) return false;

        const welcomePoints = 30; // 30 Astres offerts à l'inscription (Valable 30 jours)
        const now = Date.now();
        const expiresAt = new Date(now + 30 * 86400000).toISOString();

        const welcomeBatch: FreeAstresBatch = {
            id: `batch_welcome_${now}`,
            amount: welcomePoints,
            remaining: welcomePoints,
            earnedAt: new Date(now).toISOString(),
            expiresAt: expiresAt,
            source: 'welcome',
            description: 'Cadeau de bienvenue à la création du compte (Valable 30 jours)'
        };

        const initialTransaction: LoyaltyPointsTransaction = {
            id: `tx_reg_${now}`,
            date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }),
            amount: welcomePoints,
            type: 'bonus',
            description: 'Cadeau de bienvenue (30 Astres offerts - Validité 30 jours)'
        };

        const newUser: ClientUser = {
            id: now.toString(),
            name,
            email,
            phone,
            address,
            gender,
            birthDate,
            password: pass,
            createdAt: new Date(),
            avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${name}&gender=${gender === 'homme' ? 'male' : 'female'}`,
            history: [],
            freeAstres: welcomePoints,
            freeAstresBatches: [welcomeBatch],
            paidAstres: 0,
            loyaltyPoints: welcomePoints,
            loyaltyTier: getTierForPoints(welcomePoints),
            totalSessionMinutes: 0,
            unlockedRewards: [],
            loyaltyTransactions: [initialTransaction]
        };

        // Sauvegarde immédiate et émission réseau VPS
        users.push(newUser);
        try { localStorage.setItem('astro_clients_db', JSON.stringify(users)); } catch(e){}
        socket.emit('register_client', newUser);
        
        // Auto login
        setClient(newUser);
        try { localStorage.setItem('astro_current_client', JSON.stringify(newUser)); } catch(e){}
        try { requestPushPermissionAndSubscribe(newUser); } catch (e) {}
        return true;
    };

    const quickRegisterClient = (name: string, phone: string, email?: string, address?: string, password?: string): ClientUser => {
        const data = safeJsonParse('astro_clients_db', []);
        const users: ClientUser[] = Array.isArray(data) ? data : [];
        const cleanPhone = phone.trim();
        const cleanEmail = (email || '').trim() || `client_${cleanPhone.replace(/\D/g, '') || Date.now()}@astrox.fr`;
        const cleanAddress = (address || '').trim() || 'France';
        const cleanPassword = (password || '').trim() || ('guest_' + Math.random().toString(36).substring(2, 8));
        
        // Si un compte avec ce numéro existe déjà, on le met à jour et on le connecte
        const existing = users.find(u => u && u.phone && u.phone.replace(/\s+/g, '') === cleanPhone.replace(/\s+/g, ''));
        if (existing) {
            const updatedExisting = {
                ...existing,
                address: cleanAddress !== 'France' ? cleanAddress : (existing.address || 'France'),
                password: (cleanPassword && !cleanPassword.startsWith('guest_')) ? cleanPassword : existing.password,
                lastActiveTime: Date.now()
            };
            setClient(updatedExisting);
            try { localStorage.setItem('astro_current_client', JSON.stringify(updatedExisting)); } catch(e){}
            socket.emit('client_updated', updatedExisting);
            return updatedExisting;
        }

        const welcomePoints = 30; // 30 Astres offerts à l'inscription (Valable 30 jours)
        const now = Date.now();
        const expiresAt = new Date(now + 30 * 86400000).toISOString();

        const welcomeBatch: FreeAstresBatch = {
            id: `batch_welcome_${now}`,
            amount: welcomePoints,
            remaining: welcomePoints,
            earnedAt: new Date(now).toISOString(),
            expiresAt: expiresAt,
            source: 'welcome',
            description: 'Cadeau de bienvenue à la création du compte (Valable 30 jours)'
        };

        const initialTransaction: LoyaltyPointsTransaction = {
            id: `tx_reg_${now}`,
            date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }),
            amount: welcomePoints,
            type: 'bonus',
            description: 'Cadeau de bienvenue (30 Astres offerts - Validité 30 jours)'
        };

        const newUser: ClientUser = {
            id: now.toString(),
            name: name.trim(),
            email: cleanEmail,
            phone: cleanPhone,
            address: cleanAddress,
            gender: 'femme',
            password: cleanPassword,
            createdAt: new Date(),
            avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(name)}`,
            history: [],
            freeAstres: welcomePoints,
            freeAstresBatches: [welcomeBatch],
            paidAstres: 0,
            loyaltyPoints: welcomePoints,
            loyaltyTier: getTierForPoints(welcomePoints),
            totalSessionMinutes: 0,
            unlockedRewards: [],
            loyaltyTransactions: [initialTransaction]
        };

        users.push(newUser);
        try { localStorage.setItem('astro_clients_db', JSON.stringify(users)); } catch(e){}
        socket.emit('register_client', newUser);
        
        setClient(newUser);
        try { localStorage.setItem('astro_current_client', JSON.stringify(newUser)); } catch(e){}
        try { requestPushPermissionAndSubscribe(newUser); } catch (e) {}
        return newUser;
    };

    const logoutClient = () => {
        if (client) {
            const offlineClient = { ...client, lastActiveTime: 0 };
            const dbData = safeJsonParse('astro_clients_db', []);
            const users: ClientUser[] = Array.isArray(dbData) ? dbData : [];
            const newUsers = users.map(u => u.id === client.id ? offlineClient : u);
            localStorage.setItem('astro_clients_db', JSON.stringify(newUsers));
            socket.emit('client_updated', offlineClient);
        }
        setClient(null);
        localStorage.removeItem('astro_current_client');
    };

    const updateClientProfile = (data: Partial<ClientUser>) => {
        if (!client) return;
        const updatedClient = { ...client, ...data };
        saveClientState(updatedClient);
    };

    const addLoyaltyPoints = (amount: number, reason: string, mediumName?: string, transactionType: LoyaltyPointsTransaction['type'] = 'bonus') => {
        const currentStored = safeJsonParse<ClientUser | null>('astro_current_client', null);
        const activeClient = currentStored || client;
        if (!activeClient) return;

        const currentPoints = activeClient.loyaltyPoints || 0;
        const newPoints = Math.max(0, currentPoints + amount);

        const newTx: LoyaltyPointsTransaction = {
            id: `tx_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
            date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
            amount: amount,
            type: transactionType,
            description: reason,
            mediumName
        };

        const existingTxs = activeClient.loyaltyTransactions || [];
        const updatedClient: ClientUser = {
            ...activeClient,
            loyaltyPoints: newPoints,
            loyaltyTier: getTierForPoints(newPoints),
            loyaltyTransactions: [newTx, ...existingTxs]
        };

        saveClientState(updatedClient);
    };

        
    const deductVoiceCallPoints = (durationSeconds: number, mediumName?: string): { minutesUsed: number; pointsDeducted: number; remainingPoints: number } => {
        const currentStored = safeJsonParse<ClientUser | null>('astro_current_client', null);
        const activeClient = currentStored || client;
        if (!activeClient) return { minutesUsed: 0, pointsDeducted: 0, remainingPoints: 0 };

        // 20 Astres par minute de consultation vocale sur le site
        const minutesUsed = Math.max(1, Math.ceil(durationSeconds / 60));
        const pointsDeducted = minutesUsed * 20;

        let toDeduct = pointsDeducted;
        let freeBatches = Array.isArray(activeClient.freeAstresBatches) ? [...activeClient.freeAstresBatches] : [];
        // Trier les lots gratuits par date d'expiration (les plus proches d'expirer sont déduits en premier)
        freeBatches.sort((a, b) => new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime());

        for (let i = 0; i < freeBatches.length; i++) {
            if (toDeduct <= 0) break;
            if (freeBatches[i].remaining > 0) {
                const deduction = Math.min(freeBatches[i].remaining, toDeduct);
                freeBatches[i] = {
                    ...freeBatches[i],
                    remaining: freeBatches[i].remaining - deduction
                };
                toDeduct -= deduction;
            }
        }

        // Nettoyer les lots vides
        const updatedFreeBatches = freeBatches.filter(b => b.remaining > 0);
        const newFreeAstres = updatedFreeBatches.reduce((sum, b) => sum + b.remaining, 0);

        // Déduire le restant sur les astres payants permanents
        let currentPaid = typeof activeClient.paidAstres === 'number' ? activeClient.paidAstres : 0;
        if (toDeduct > 0) {
            currentPaid = Math.max(0, currentPaid - toDeduct);
        }

        const newTotalPoints = newFreeAstres + currentPaid;

        const newTx: LoyaltyPointsTransaction = {
            id: `tx_voice_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
            date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
            amount: -pointsDeducted,
            type: 'session_earned',
            description: `Consultation Vocale Directe (${minutesUsed} min à 20 Astres/min = -${pointsDeducted} Astres)`,
            mediumName
        };

        const existingTxs = activeClient.loyaltyTransactions || [];
        const totalMinutes = (activeClient.totalSessionMinutes || 0) + minutesUsed;

        const updatedClient: ClientUser = {
            ...activeClient,
            freeAstres: newFreeAstres,
            freeAstresBatches: updatedFreeBatches,
            paidAstres: currentPaid,
            loyaltyPoints: newTotalPoints,
            loyaltyTier: getTierForPoints(newTotalPoints),
            totalSessionMinutes: totalMinutes,
            loyaltyTransactions: [newTx, ...existingTxs]
        };

        saveClientState(updatedClient);
        return { minutesUsed, pointsDeducted, remainingPoints: newTotalPoints };
    };

    const recordSessionDurationAndPoints = (sessionId: string, durationMinutes: number, mediumName: string, mediumImage: string, mediumId?: number | string, messages?: ChatMessage[]): number => {
        const currentStored = safeJsonParse<ClientUser | null>('astro_current_client', null);
        const activeClient = currentStored || client;
        if (!activeClient) return 0;

        const tier = activeClient.loyaltyTier || 'Bronze';
        const multiplier = TIER_CONFIG[tier]?.multiplier || 1.0;
        
        // 10 points par minute de session + multiplicateur de rang
        const rawPoints = Math.max(10, Math.round(durationMinutes * 10));
        const earnedPoints = Math.round(rawPoints * multiplier);

        const durationFormatted = durationMinutes >= 1 ? `${Math.round(durationMinutes)} min` : '< 1 min';
        
        // Mettre à jour l'historique
        const existingHistory = activeClient.history || [];
        const filteredHistory = existingHistory.filter(h => h.id !== sessionId);

        const historyEntry: InteractionHistory = {
            id: sessionId,
            mediumId,
            mediumName,
            mediumImage,
            date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }),
            duration: durationFormatted,
            notes: `Consultation en direct (+${earnedPoints} pts fidélité)`,
            pointsEarned: earnedPoints,
            messages: messages || []
        };

        const updatedHistory = [historyEntry, ...filteredHistory];
        const newTotalMinutes = (activeClient.totalSessionMinutes || 0) + durationMinutes;
        const currentPoints = activeClient.loyaltyPoints || 0;
        const newPoints = currentPoints + earnedPoints;

        const tx: LoyaltyPointsTransaction = {
            id: `tx_sess_${Date.now()}`,
            date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
            amount: earnedPoints,
            type: 'session_earned',
            description: `Session (${durationFormatted}) avec ${mediumName}`,
            mediumName
        };

        const updatedClient: ClientUser = {
            ...activeClient,
            history: updatedHistory,
            totalSessionMinutes: Math.round(newTotalMinutes * 10) / 10,
            loyaltyPoints: newPoints,
            loyaltyTier: getTierForPoints(newPoints),
            loyaltyTransactions: [tx, ...(activeClient.loyaltyTransactions || [])]
        };

        saveClientState(updatedClient);
        return earnedPoints;
    };

    const recordDivinationActivity = (
        type: 'tarot' | 'dreams' | 'love' | 'horoscope' | 'numerology' | 'crystal',
        title: string,
        summary: string,
        details?: any
    ) => {
        const currentStored = safeJsonParse<ClientUser | null>('astro_current_client', null);
        const activeClient = currentStored || client;
        if (!activeClient) return;

        const entryId = `div_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
        const historyEntry: InteractionHistory = {
            id: entryId,
            mediumName: `Oracle AstroX (${title})`,
            date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }),
            duration: 'Oracle en direct',
            notes: summary,
            pointsEarned: 5,
            messages: []
        };

        const existingHistory = activeClient.history || [];
        const updatedHistory = [historyEntry, ...existingHistory];
        const newPoints = (activeClient.loyaltyPoints || 0) + 5;

        const tx: LoyaltyPointsTransaction = {
            id: `tx_div_${Date.now()}`,
            date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
            amount: 5,
            type: 'bonus',
            description: `Tirage ${title} réalisé (+5 Astres)`
        };

        const updatedClient: ClientUser = {
            ...activeClient,
            history: updatedHistory,
            loyaltyPoints: newPoints,
            loyaltyTier: getTierForPoints(newPoints),
            loyaltyTransactions: [tx, ...(activeClient.loyaltyTransactions || [])]
        };

        saveClientState(updatedClient);

        // Sauvegarder dans la mémoire IA (Brain) pour que les médiums et l'IA connaissent ses tirages et questions
        try {
            socket.emit('save_ai_memory', {
                clientId: activeClient.id,
                clientName: activeClient.name,
                clientPhone: activeClient.phone,
                memoryData: {
                    lastTopic: title,
                    lastQuestion: summary,
                    divinationType: type,
                    details: details || {},
                    timestamp: Date.now()
                }
            });
        } catch (e) {
            console.warn('Erreur émission save_ai_memory:', e);
        }
    };

    

    const claimDailyBonus = (): { success: boolean; pointsAwarded: number; message: string } => {
        const currentStored = safeJsonParse<ClientUser | null>('astro_current_client', null);
        const activeClient = currentStored || client;
        if (!activeClient) return { success: false, pointsAwarded: 0, message: "Vous devez être connecté pour réclamer votre bonus." };

        const todayStr = new Date().toLocaleDateString('en-CA');
        const lockKey = `astro_daily_claimed_${activeClient.id}_${todayStr}`;
        const isLockedInStorage = typeof window !== 'undefined' && localStorage.getItem(lockKey) === 'true';

        if (activeClient.claimedDailyBonusDate === todayStr || isLockedInStorage) {
            return { 
                success: false, 
                pointsAwarded: 0, 
                message: "Vous avez déjà récupéré votre bonus quotidien aujourd'hui (1 seule fois par jour). Revenez demain !" 
            };
        }

        // Verrouillage persistant strict immédiat
        try { localStorage.setItem(lockKey, 'true'); } catch(e){}

        const bonusPoints = 2; // Exactement 2 Astres par jour (Valable 30 jours)
        const now = Date.now();
        const expiresAt = new Date(now + 30 * 86400000).toISOString();

        const dailyBatch: FreeAstresBatch = {
            id: `batch_daily_${now}`,
            amount: bonusPoints,
            remaining: bonusPoints,
            earnedAt: new Date(now).toISOString(),
            expiresAt: expiresAt,
            source: 'daily',
            description: 'Bonus de connexion quotidienne (Valable 30 jours)'
        };

        const existingBatches = Array.isArray(activeClient.freeAstresBatches) ? [...activeClient.freeAstresBatches] : [];
        existingBatches.push(dailyBatch);

        const currentFree = typeof activeClient.freeAstres === 'number' ? activeClient.freeAstres : 0;
        const currentPaid = typeof activeClient.paidAstres === 'number' ? activeClient.paidAstres : 0;
        const newFree = currentFree + bonusPoints;
        const newTotal = newFree + currentPaid;

        const newTx: LoyaltyPointsTransaction = {
            id: `tx_daily_${now}`,
            date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
            amount: bonusPoints,
            type: 'daily_login',
            description: 'Bonus de connexion quotidienne (+2 Astres offerts - Validité 30 jours)'
        };

        const existingTxs = activeClient.loyaltyTransactions || [];
        const updatedClient: ClientUser = {
            ...activeClient,
            freeAstres: newFree,
            freeAstresBatches: existingBatches,
            paidAstres: currentPaid,
            loyaltyPoints: newTotal,
            loyaltyTier: getTierForPoints(newTotal),
            claimedDailyBonusDate: todayStr,
            loyaltyTransactions: [newTx, ...existingTxs]
        };

        saveClientState(updatedClient);
        return { success: true, pointsAwarded: bonusPoints, message: `Félicitations ! Vous avez reçu +${bonusPoints} Astres quotidiens offerts (valables 30 jours)` };
    };

    const buyPack = (packType: '50' | '100' | '150' | string, transcashCode?: string): { success: boolean; minutesAdded: number; pointsAdded: number } => {
        const currentStored = safeJsonParse<ClientUser | null>('astro_current_client', null);
        const activeClient = currentStored || client;
        if (!activeClient) return { success: false, minutesAdded: 0, pointsAdded: 0 };

        const packConfig = (TRANSCASH_PACKS_CONFIG as any)[packType] || TRANSCASH_PACKS_CONFIG['50'];
        const totalAstres = packConfig.totalAstres;
        const minutesAdded = Math.floor(totalAstres / 20); // 20 astres par minute
        const now = Date.now();

        const newPack: PurchasedPack = {
            id: `pack_${now}_${Math.random().toString(36).substring(2, 6)}`,
            packType: packType,
            name: packConfig.name,
            price: packConfig.price,
            baseAstres: packConfig.baseAstres,
            bonusAstres: packConfig.bonusAstres,
            totalAstres: totalAstres,
            minutes: minutesAdded,
            purchaseDate: new Date(now).toISOString(),
            transcashCode,
            status: 'active'
        };

        const existingPacks = activeClient.purchasedPacks || [];
        const currentPaid = typeof activeClient.paidAstres === 'number' ? activeClient.paidAstres : 0;
        const currentFree = typeof activeClient.freeAstres === 'number' ? activeClient.freeAstres : (activeClient.loyaltyPoints || 0);
        
        // Les astres achetés sont ajoutés au solde permanent (n'expirent jamais)
        const newPaid = currentPaid + totalAstres;
        const newTotalPoints = currentFree + newPaid;

        const newTx: LoyaltyPointsTransaction = {
            id: `tx_pack_${now}`,
            date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
            amount: totalAstres,
            type: 'purchase',
            description: `Recharge Transcash ${packConfig.price}€ (+${totalAstres} Astres permanents dont ${packConfig.bonusAstres} offerts)`
        };

        const existingTxs = activeClient.loyaltyTransactions || [];
        const updatedClient: ClientUser = {
            ...activeClient,
            paidAstres: newPaid,
            freeAstres: currentFree,
            loyaltyPoints: newTotalPoints,
            loyaltyTier: getTierForPoints(newTotalPoints),
            purchasedPacks: [newPack, ...existingPacks],
            loyaltyTransactions: [newTx, ...existingTxs]
        };

        saveClientState(updatedClient);
        return { success: true, minutesAdded, pointsAdded: totalAstres };
    };

    const redeemReward = (rewardId: string): { success: boolean; message: string } => {
        const currentStored = safeJsonParse<ClientUser | null>('astro_current_client', null);
        const activeClient = currentStored || client;
        if (!activeClient) return { success: false, message: "Veuillez vous connecter." };

        const reward = DEFAULT_LOYALTY_REWARDS.find(r => r.id === rewardId);
        if (!reward) return { success: false, message: "Récompense introuvable." };

        const currentPoints = activeClient.loyaltyPoints || 0;
        if (currentPoints < reward.cost) {
            return { 
                success: false, 
                message: `Astres insuffisants. Il vous manque ${reward.cost - currentPoints} Astres pour débloquer cette récompense.` 
            };
        }

        const newPoints = currentPoints - reward.cost;
        const unlocked = activeClient.unlockedRewards || [];

        const tx: LoyaltyPointsTransaction = {
            id: `tx_redeem_${Date.now()}`,
            date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
            amount: -reward.cost,
            type: 'reward_redeemed',
            description: `Astres convertis : ${reward.title} `
        };

        const recentRewardInfo = {
            id: reward.id,
            title: reward.title,
            description: reward.description,
            cost: reward.cost,
            icon: reward.icon,
            category: reward.category,
            badge: reward.badge,
            unlockedAt: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
        };

        const updatedClient: ClientUser = {
            ...activeClient,
            loyaltyPoints: newPoints,
            loyaltyTier: getTierForPoints(newPoints),
            unlockedRewards: [...unlocked, rewardId],
            recentUnlockedReward: recentRewardInfo,
            loyaltyTransactions: [tx, ...(activeClient.loyaltyTransactions || [])]
        };

        // Enregistrer une notification de conversion pour l'administration
        const newNotification = {
            id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
            clientId: activeClient.id,
            clientName: activeClient.name,
            clientPhone: activeClient.phone,
            clientEmail: activeClient.email,
            rewardId: reward.id,
            rewardTitle: reward.title,
            rewardCost: reward.cost,
            rewardDescription: reward.description,
            date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
            status: 'pending' as const
        };

        const savedNotifs = safeJsonParse<any[]>('astro_admin_notifications', []);
        localStorage.setItem('astro_admin_notifications', JSON.stringify([newNotification, ...savedNotifs]));

        // Déclencher un événement global pour une mise à jour instantanée du tableau de bord Admin
        window.dispatchEvent(new CustomEvent('astro_admin_notifications_updated'));
        
        // Transmettre au serveur VPS pour notification en direct sur tous les tableaux de bord admin (Onglet Conversions)
        socket.emit('new_conversion', newNotification);

        // Transmettre aussi dans la Boîte de Réception VPS pour visibilité maximale
        socket.emit('new_submission', {
            id: newNotification.id,
            date: new Date().toISOString(),
            type: "Conversion d'Astres",
            name: activeClient.name,
            phone: activeClient.phone,
            email: activeClient.email,
            details: `Demande de conversion : ${reward.title} (${reward.cost} Astres)`,
            rewardTitle: reward.title,
            rewardCost: reward.cost,
            status: 'new'
        });

        // Poster également un message automatique dans la discussion avec Sarah la secrétaire
        const sarahSessionId = `sarah-${activeClient.id}`;
        socket.emit('send_message', {
            sessionId: sarahSessionId,
            message: {
                id: `conversion_msg_${Date.now()}`,
                role: 'user',
                text: `**Demande de conversion d'Astres** : J'ai converti **${reward.cost} Astres** pour la récompense **"${reward.title}"** (${reward.description}). Merci de bien vouloir la valider !`,
                timestamp: new Date()
            }
        });

        saveClientState(updatedClient);
        return {
            success: true,
            message: `Bravo ! Vous avez converti vos Astres pour "${reward.title}". L'administration a été notifiée et validera votre transaction très prochainement.`
        };
    };

    const addHistoryEntry = (entry: InteractionHistory) => {
        const currentStored = safeJsonParse<ClientUser | null>('astro_current_client', null);
        const activeClient = currentStored || client;
        
        if (!activeClient) return;

        const existingHistory = activeClient.history || [];
        
        // --- FIX: Empêcher les doublons (Même ID de session) ---
        if (existingHistory.some(h => h.id === entry.id)) {
            return; // Déjà enregistré
        }

        const updatedHistory = [entry, ...existingHistory];
        const updatedClient = { ...activeClient, history: updatedHistory };
        saveClientState(updatedClient);
    };

    const openAuthModal = (prefillData?: Partial<ClientUser>) => {
        if (prefillData) {
            setAuthPrefill(prefillData);
        } else {
            setAuthPrefill(null);
        }
        setIsAuthModalOpen(true);
    };
    
    const closeAuthModal = () => {
        setIsAuthModalOpen(false);
    };

    // --- Reviews Logic ---
    const addReview = (mediumId: number, rating: number, text: string, guestName?: string) => {
        const currentStored = safeJsonParse<ClientUser | null>('astro_current_client', null);
        const activeClient = currentStored || client;
        const authorName = activeClient ? activeClient.name : (guestName || "Anonyme");
        
        const allReviews = safeJsonParse<any>('astro_real_reviews', {});
        const mediumReviews = (allReviews && Array.isArray(allReviews[mediumId])) ? allReviews[mediumId] : [];

        const newReview: Review = {
            id: `real-${Date.now()}`,
            author: authorName,
            rating: rating,
            text: text, 
            date: "À l'instant",
            timestamp: Date.now(),
            isReal: true,
            read: false, 
            mediumId: mediumId
        };

        const updatedAllReviews = { ...allReviews, [mediumId]: [newReview, ...mediumReviews] };
        localStorage.setItem('astro_real_reviews', JSON.stringify(updatedAllReviews));
        socket.emit('add_review', newReview);

        // Récompenser avec 5 Astres pour avoir évalué la discussion (limité à 1 fois par jour)
        if (activeClient) {
            const todayStr = new Date().toISOString().split('T')[0];
            const alreadyClaimedToday = activeClient.lastReviewBonusDate === todayStr;

            if (!alreadyClaimedToday) {
                const reviewBonusPoints = 5;
                const currentPoints = activeClient.loyaltyPoints || 0;
                const newPoints = currentPoints + reviewBonusPoints;

                const tx: LoyaltyPointsTransaction = {
                    id: `tx_eval_${Date.now()}`,
                    date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
                    amount: reviewBonusPoints,
                    type: 'bonus',
                    description: "Évaluation de consultation (+5 Astres, bonus quotidien)"
                };

                const existingTxs = activeClient.loyaltyTransactions || [];
                const updatedClient: ClientUser = {
                    ...activeClient,
                    loyaltyPoints: newPoints,
                    loyaltyTier: getTierForPoints(newPoints),
                    lastReviewBonusDate: todayStr,
                    loyaltyTransactions: [tx, ...existingTxs]
                };

                saveClientState(updatedClient);
            }
        }

        // Émettre un événement GLOBAL pour le LiveActivityFeed
        const event = new CustomEvent('astro_new_rating', { 
            detail: { mediumId, author: authorName, rating, text } 
        });
        window.dispatchEvent(event);
    };

    const getRealReviews = (mediumId: number): Review[] => {
        const allReviews = safeJsonParse<any>('astro_real_reviews', {});
        if (typeof allReviews === 'object' && allReviews !== null && Array.isArray(allReviews[mediumId])) {
            return allReviews[mediumId];
        }
        return [];
    };

    

// Daily bonus is manually claimed by user click only (no auto-claim)

    // 2. Real-time active status tracking with throttle + VPS heartbeat
    useEffect(() => {
        if (!client) return;

        let lastUpdate = 0;
        let lastServerSync = 0;
        const updateActivity = () => {
            const now = Date.now();
            if (now - lastUpdate < 10000) return;
            lastUpdate = now;

            const currentStored = safeJsonParse<ClientUser | null>('astro_current_client', null);
            if (currentStored && currentStored.id === client.id) {
                const updatedClient: ClientUser = {
                    ...currentStored,
                    lastActiveTime: now
                };
                
                localStorage.setItem('astro_current_client', JSON.stringify(updatedClient));
                
                const dbData = safeJsonParse('astro_clients_db', []);
                const users: ClientUser[] = Array.isArray(dbData) ? dbData : [];
                const newUsers = users.map(u => u.id === updatedClient.id ? updatedClient : u);
                localStorage.setItem('astro_clients_db', JSON.stringify(newUsers));

                // Heartbeat VPS toutes les 60 secondes pour que l'admin voie le client "en ligne"
                if (now - lastServerSync > 60000) {
                    lastServerSync = now;
                    socket.emit('client_updated', updatedClient);
                }
            }
        };

        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
        events.forEach(name => window.addEventListener(name, updateActivity));

        updateActivity();

        const handleUnload = () => {
            const currentStored = safeJsonParse<ClientUser | null>('astro_current_client', null);
            if (currentStored && currentStored.id === client.id) {
                const offlineClient = { ...currentStored, lastActiveTime: 0 };
                const dbData = safeJsonParse('astro_clients_db', []);
                const users: ClientUser[] = Array.isArray(dbData) ? dbData : [];
                const newUsers = users.map(u => u.id === currentStored.id ? offlineClient : u);
                localStorage.setItem('astro_clients_db', JSON.stringify(newUsers));
                socket.emit('client_updated', offlineClient);
            }
        };
        window.addEventListener('beforeunload', handleUnload);

        return () => {
            events.forEach(name => window.removeEventListener(name, updateActivity));
            window.removeEventListener('beforeunload', handleUnload);
        };
    }, [client?.id]);

    return (
        <ClientContext.Provider value={{
            client,
            loginClient,
            registerClient,
            quickRegisterClient,
            logoutClient,
            updateClientProfile,
            addHistoryEntry,
            isAuthModalOpen,
            openAuthModal,
            closeAuthModal,
            authPrefill,
            addReview,
            getRealReviews,
            addLoyaltyPoints,
            recordSessionDurationAndPoints,
            recordDivinationActivity,
            deductVoiceCallPoints,
                    claimDailyBonus,
            redeemReward,
            rewardsCatalog: DEFAULT_LOYALTY_REWARDS,
            buyPack
        }}>
            {children}
        </ClientContext.Provider>
    );
};

export const useClient = () => {
    const context = useContext(ClientContext);
    if (!context) throw new Error("useClient must be used within ClientProvider");
    return context;
};